import React, { useEffect, useMemo, useRef, useState } from "react";
import SectionCard from "../ui/SectionCard";
import Button from "../../../../components/Button/Button.jsx";
import {
  getExpenseAttachmentsByClaim,
  uploadExpenseAttachmentToClaim,
  uploadExpenseAttachmentToItem,
  deleteExpenseAttachment,
} from "../../../../services/expenseServices";
import {
  FaPaperclip,
  FaUpload,
  FaTrash,
  FaExternalLinkAlt,
  FaSyncAlt,
} from "react-icons/fa";

const fmtSize = (n) => {
  const v = Number(n || 0);
  if (!v) return "—";
  if (v < 1024) return `${v} o`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} Ko`;
  return `${(v / (1024 * 1024)).toFixed(1)} Mo`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("fr-FR", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function ExpenseAttachmentsCard({
  claimId,
  items = [], // lignes de frais (optionnel) pour lier un justificatif à une ligne
  onEnsureClaim, // créer/enregistrer la fiche si besoin
  onChanged, // callback -> refresh totals/claim
}) {
  const fileRef = useRef(null);

  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  // liaison optionnelle à une ligne
  const [linkItemId, setLinkItemId] = useState("");

  const itemsOptions = useMemo(() => {
    const arr = Array.isArray(items) ? items : [];
    return arr.map((it) => ({
      id: it.id,
      label: `#${it.id} • ${it.type}${it.date_frais ? ` • ${it.date_frais}` : ""} • ${Number(
        it.montant || 0,
      ).toFixed(2)}€`,
    }));
  }, [items]);

  const load = async () => {
    if (!claimId) return;
    try {
      setError("");
      setLoading(true);
      const data = await getExpenseAttachmentsByClaim(claimId);
      setAttachments(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setError("Impossible de charger les justificatifs.");
      setAttachments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  const triggerPick = () => fileRef.current?.click();

  const handlePickedFiles = async (files) => {
    if (!claimId) return;
    const list = Array.from(files || []);
    if (list.length === 0) return;

    try {
      setError("");
      setUploading(true);

      // upload fichier par fichier (simple & robuste)
      for (const f of list) {
        const fd = new FormData();
        // 👉 IMPORTANT : ton backend multer doit lire "file"
        fd.append("file", f);

        if (linkItemId) {
          await uploadExpenseAttachmentToItem(Number(linkItemId), fd);
        } else {
          await uploadExpenseAttachmentToClaim(claimId, fd);
        }
      }

      await load();
      onChanged?.();
    } catch (e) {
      console.error(e);
      setError("Erreur lors de l’upload du justificatif.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (att) => {
    if (!window.confirm("Supprimer ce justificatif ?")) return;
    try {
      setError("");
      await deleteExpenseAttachment(att.id);
      await load();
      onChanged?.();
    } catch (e) {
      console.error(e);
      setError("Erreur lors de la suppression du justificatif.");
    }
  };

  return (
    <SectionCard
      id="justificatifs"
      title="Justificatifs"
      subtitle="Ajoutez vos justificatifs (photos, PDF…). Vous pouvez les lier à une ligne de frais (optionnel)."
      icon={<FaPaperclip />}
      actions={
        <div className="flex flex-col gap-2">
          <Button
            text={loading ? "Chargement..." : "Rafraîchir"}
            variant="outline"
            size="sm"
            onClick={load}
            icon={FaSyncAlt}
            disabled={!claimId}
          />
          <Button
            text={uploading ? "Upload..." : "Ajouter"}
            variant="primary"
            size="sm"
            onClick={triggerPick}
            icon={FaUpload}
            disabled={!claimId || uploading}
          />
        </div>
      }
    >
      {!claimId ? (
        <div className="rounded-xl p-4 bg-amber-50 text-amber-900 ring-1 ring-amber-200">
          Pour ajouter des justificatifs, il faut d’abord <strong>enregistrer la fiche</strong> (pour obtenir un id).
          <div className="mt-3">
            <button
              type="button"
              className="h-10 px-4 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700"
              onClick={onEnsureClaim}
            >
              Enregistrer maintenant
            </button>
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="mt-3 rounded-xl p-3 bg-rose-50 text-rose-800 ring-1 ring-rose-200">
          {error}
        </div>
      ) : null}

      {/* Upload controls */}
      {claimId ? (
        <div className="mt-4 rounded-2xl ring-1 ring-slate-200 bg-white p-4">
          <div className="grid gap-3 md:grid-cols-2 items-end">
            <label className="block">
              <div className="text-sm font-semibold text-slate-700 mb-1">
                Lier à une ligne (optionnel)
              </div>
              <select
                value={linkItemId}
                onChange={(e) => setLinkItemId(e.target.value)}
                className="w-full rounded-xl px-3 py-2 ring-1 ring-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">— Justificatif global (fiche) —</option>
                {itemsOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
              <div className="text-xs text-slate-500 mt-1">
                Si tu lies à une ligne, on utilisera l’endpoint <code>/items/:itemId/attachments</code>.
              </div>
            </label>

            <div className="flex gap-2">
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                multiple
                accept="image/*,application/pdf"
                onChange={(e) => handlePickedFiles(e.target.files)}
              />
              <button
                type="button"
                onClick={triggerPick}
                disabled={uploading}
                className={[
                  "h-11 px-4 rounded-xl inline-flex items-center gap-2 ring-1 transition",
                  uploading
                    ? "bg-slate-200 text-slate-500 ring-slate-200"
                    : "bg-indigo-600 text-white ring-indigo-700 hover:bg-indigo-700",
                ].join(" ")}
              >
                <FaUpload />
                {uploading ? "Upload..." : "Choisir des fichiers"}
              </button>

              <button
                type="button"
                onClick={() => {
                  setLinkItemId("");
                }}
                className="h-11 px-4 rounded-xl ring-1 ring-slate-200 bg-slate-100 hover:bg-slate-200"
              >
                Reset lien
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* List */}
      {claimId ? (
        <div className="mt-4 rounded-2xl ring-1 ring-slate-200 bg-white overflow-hidden">
          <div className="p-3 bg-slate-50 flex items-center justify-between">
            <div className="text-sm font-semibold">
              {attachments.length} justificatif(s)
            </div>
            <div className="text-xs text-slate-500">Fiche #{claimId}</div>
          </div>

          {loading ? (
            <div className="p-4 text-sm text-slate-600">Chargement…</div>
          ) : attachments.length === 0 ? (
            <div className="p-4 text-sm text-slate-600">
              Aucun justificatif pour le moment.
            </div>
          ) : (
            <div className="max-h-[420px] overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-white ring-1 ring-slate-200">
                  <tr className="text-left">
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Fichier</th>
                    <th className="px-3 py-2">Lié à</th>
                    <th className="px-3 py-2 text-right">Taille</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {attachments.map((a) => (
                    <tr key={a.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 whitespace-nowrap">
                        {fmtDate(a.created_at)}
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium">
                          {a.original_name || `Fichier #${a.id}`}
                        </div>
                        <div className="text-xs text-slate-500">
                          {a.mime_type || "—"}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {a.expense_claim_item_id ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-sky-100 text-sky-800 ring-1 ring-sky-200">
                            Ligne #{a.expense_claim_item_id}
                          </span>
                        ) : (
                          <span className="text-slate-500">Fiche</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {fmtSize(a.size_bytes)}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-2">
                          <a
                            href={a.file_url}
                            target="_blank"
                            rel="noreferrer"
                            className="h-9 px-3 rounded-xl ring-1 ring-slate-200 bg-white hover:bg-slate-50 inline-flex items-center gap-2"
                          >
                            <FaExternalLinkAlt />
                            Ouvrir
                          </a>
                          <button
                            type="button"
                            onClick={() => remove(a)}
                            className="h-9 px-3 rounded-xl bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-2"
                          >
                            <FaTrash />
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}
    </SectionCard>
  );
}
